/**
 * @file     TypewriterText.tsx
 * @purpose  ChatGPT-style progressive reveal for the newest assistant reply.
 *           Purely visual: the full text already arrived; characters are
 *           revealed on a rAF loop, then onDone fires.
 */
'use client';

import { useEffect, useRef, useState } from 'react';

/** Characters revealed per animation frame (~60fps -> ~180 chars/s). */
const CHARS_PER_FRAME = 3;

export function TypewriterText({
  text,
  animate,
  onDone,
}: {
  text: string;
  animate: boolean;
  onDone?: () => void;
}) {
  const [shown, setShown] = useState(animate ? 0 : text.length);
  const shownRef = useRef(animate ? 0 : text.length);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!animate) {
      shownRef.current = text.length;
      setShown(text.length);
      return;
    }
    // Respect reduced-motion: reveal the full reply at once, then signal done.
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      shownRef.current = text.length;
      setShown(text.length);
      onDone?.();
      return;
    }
    // Restart from zero if animation begins after mount (prop flip).
    if (!startedRef.current) {
      startedRef.current = true;
      shownRef.current = 0;
      setShown(0);
    }
    let raf = 0;
    let done = false;
    // Progress lives in a ref so onDone fires from the rAF callback, never
    // inside a state-updater (setState during render warns and can misfire).
    const tick = () => {
      const next = Math.min(shownRef.current + CHARS_PER_FRAME, text.length);
      shownRef.current = next;
      setShown(next);
      if (next < text.length) {
        raf = requestAnimationFrame(tick);
      } else if (!done) {
        done = true;
        onDone?.();
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, animate]);

  return <>{text.slice(0, shown)}</>;
}
